<template>
  <div class="company-logo" :class="logoClasses">
    <img 
      v-if="displayUrl && !hasImageError" 
      :src="displayUrl" 
      :alt="companyName || 'Unternehmenslogo'" 
      @error="handleImageError" 
      class="img-fluid"
    />
    <div v-else-if="!displayUrl && !hasImageError" class="placeholder-logo" :style="{ backgroundColor: logoColor }">
      {{ logoInitial }}
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { API_BASE_URL } from '@/config/api';

export default defineComponent({
  name: 'CompanyLogo',
  props: {
    company: {
      type: Object,
      default: () => ({})
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'large'].includes(value)
    },
    showDefault: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loadedCompany: null,
      loadedLogoUrl: null,
      hasImageError: false
    };
  },
  computed: {
    logoUrl() {
      if (this.company && typeof this.company === 'object') {
        // Debug-Ausgabe
        console.log('Logo für Company geladen:', this.company);
        
        // Prüfe, ob logo_url direkt im company-Objekt ist
        if (this.company.logo_url) {
          return this.company.logo_url;
        }
        
        // Bei impersonierter Firma könnte logo in company.company sein
        if (this.company.company && this.company.company.logo_url) {
          return this.company.company.logo_url;
        }
      }
      
      return null;
    },
    
    companyName() {
      if (this.company) {
        if (typeof this.company === 'string') {
          return this.company;
        }
        
        if (this.company.name) {
          return this.company.name;
        }
        
        if (this.company.company && this.company.company.name) {
          return this.company.company.name;
        }
      }
      
      return '';
    },
    
    hasLogo() {
      return !!this.logoUrl;
    },
    
    logoClasses() {
      return {
        'company-logo': true,
        [`company-logo-${this.size}`]: true,
        'with-logo': this.hasLogo,
        'with-text': !this.hasLogo && this.showDefault
      };
    },
    
    // CSS-Klasse basierend auf der Größe
    sizeClass() {
      return `logo-${this.size}`;
    },
    
    // Effektive Logo-URL mit Fallback-Logik
    effectiveLogoUrl() {
      // Wenn explizite URL vorhanden und kein Fehler
      if (this.logoUrl && !this.hasImageError) {
        return this.logoUrl;
      }
      
      // Verwende normalisierte Firma
      const company = this.normalizedCompany;
      
      // Wenn Company-Objekt vorhanden und kein Fehler
      if (company && typeof company === 'object' && company.logo_url && !this.hasImageError) {
        return company.logo_url;
      }
      
      // Wenn geladene URL vorhanden und kein Fehler
      if (this.loadedLogoUrl && !this.hasImageError) {
        return this.loadedLogoUrl;
      }
      
      return null;
    },
    
    // Tatsächlich anzuzeigende Logo-URL (mit API-URL-Präfix, falls nötig)
    displayUrl() {
      const url = this.effectiveLogoUrl; // Hole die effektive URL
      if (!url) return null;
      
      // Prüfe, ob die URL bereits vollständig ist
      if (url.startsWith('http')) {
        return url;
      }
      
      // Füge API-Basis-URL hinzu (nur wenn nicht leer)
      return API_BASE_URL ? `${API_BASE_URL}/${url.replace(/^\//, '')}` : url;
    },
    
    // Initial für Platzhalter-Logo
    logoInitial() {
      const name = this.getCompanyName();
      return name ? name.charAt(0).toUpperCase() : '?';
    },
    
    // Hintergrundfarbe für Platzhalter-Logo
    logoColor() {
      const name = this.getCompanyName();
      if (!name) return '#3490dc'; // Fallback-Farbe
      
      // Generiere eine deterministische Farbe basierend auf dem Firmennamen
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      
      const hue = hash % 360;
      return `hsl(${hue}, 70%, 60%)`;
    },
    
    // Falls company ein Objekt mit embedded company ist (häufiger Fall bei APIs)
    normalizedCompany() {
      if (this.company && this.company.company) {
        return this.company.company;
      }
      return this.company;
    }
  },
  methods: {
    getInitials() {
      if (!this.companyName) return '';
      
      const initials = this.companyName
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase();
      
      return initials.slice(0, 2);
    },
    handleImageError(event) {
      console.error('Fehler beim Laden des Logos:', this.displayUrl, 'Event:', event);
      this.hasImageError = true;
    },
    
    // Firmenname aus verschiedenen Quellen ermitteln
    getCompanyName() {
      if (this.companyName) return this.companyName;
      if (this.company && typeof this.company === 'object' && this.company.name) {
        return this.company.name;
      }
      if (this.loadedCompany && this.loadedCompany.name) {
        return this.loadedCompany.name;
      }
      return '';
    },
    
    // Company-Daten laden, wenn nur eine ID übergeben wurde
    loadCompanyData() {
      // Vereinfachte Version ohne Store-Zugriff
      console.log('Logo für Company geladen:', this.company);
    }
  },
  watch: {
    company: {
      handler(newVal, oldVal) {
        // Setze Fehler zurück, wenn sich die Firma ÄNDERT
        // Prüfe, ob sich die relevanten Teile geändert haben, um unnötiges Zurücksetzen zu vermeiden
        const oldLogo = oldVal?.logo_url || oldVal?.company?.logo_url;
        const newLogo = newVal?.logo_url || newVal?.company?.logo_url;
        if (oldLogo !== newLogo) {
           this.hasImageError = false;
           console.log('Logo-URL hat sich geändert, hasImageError zurückgesetzt.');
        }
        this.loadCompanyData();
      },
      deep: true,
      immediate: true // Sofort beim Initialisieren ausführen
    }
  }
});
</script>

<style lang="scss" scoped>
.company-logo {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  
  &.logo-sm {
    width: 32px;
    height: 32px;
    
    .placeholder-logo {
      font-size: 16px;
    }
  }
  
  &.logo-md {
    width: 48px;
    height: 48px;
    
    .placeholder-logo {
      font-size: 20px;
    }
  }
  
  &.logo-lg {
    width: 64px;
    height: 64px;
    
    .placeholder-logo {
      font-size: 24px;
    }
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  
  .placeholder-logo {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: white;
    font-weight: bold;
  }
}
</style> 